import { useCallback, useEffect, useState } from 'react';
import { useAppUpdate } from '../contexts/AppUpdateContext';
import { isChunkLoadFailure } from '../services/appUpdate';
import { getTaskTypeDescriptor, TaskTypeDescriptor } from './descriptor';
import { TaskTypeModule } from './module';

export type TaskTypeRuntimeStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface TaskTypeRuntimeState {
  descriptor: TaskTypeDescriptor | null;
  module: TaskTypeModule<any, any> | null;
  status: TaskTypeRuntimeStatus;
  error: Error | null;
  staleDeployment: boolean;
  retry: () => void;
}

/**
 * Resolve exactly one selected task runtime. The loaded id is tracked
 * separately from the requested id so a route change cannot render the
 * previous task view for one frame while the new chunk is loading.
 */
export function useTaskTypeRuntime(taskTypeId: string | null | undefined): TaskTypeRuntimeState {
  const descriptor = taskTypeId ? getTaskTypeDescriptor(taskTypeId) : null;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{
    requestedId: string | null;
    loadedId: string | null;
    status: TaskTypeRuntimeStatus;
    module: TaskTypeModule<any, any> | null;
    error: Error | null;
    staleDeployment: boolean;
  }>({
    requestedId: null,
    loadedId: null,
    status: 'idle',
    module: null,
    error: null,
    staleDeployment: false,
  });
  const { requestReload } = useAppUpdate();

  useEffect(() => {
    if (!taskTypeId || !descriptor) {
      setState({
        requestedId: null,
        loadedId: null,
        status: 'idle',
        module: null,
        error: null,
        staleDeployment: false,
      });
      return undefined;
    }

    let active = true;
    setState({
      requestedId: taskTypeId,
      loadedId: null,
      status: 'loading',
      module: null,
      error: null,
      staleDeployment: false,
    });
    descriptor.loadRuntime().then((module) => {
      if (!active) return;
      setState({
        requestedId: taskTypeId,
        loadedId: taskTypeId,
        status: 'ready',
        module,
        error: null,
        staleDeployment: false,
      });
    }).catch((rawError: unknown) => {
      if (!active) return;
      const error = rawError instanceof Error ? rawError : new Error(String(rawError));
      const staleDeployment = isChunkLoadFailure(error);
      if (staleDeployment) requestReload?.('chunk-load-error');
      setState({
        requestedId: taskTypeId,
        loadedId: null,
        status: 'error',
        module: null,
        error,
        staleDeployment,
      });
    });
    return () => {
      active = false;
    };
  }, [attempt, descriptor, requestReload, taskTypeId]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const current = state.requestedId === taskTypeId
    ? state
    : taskTypeId
      ? {
        requestedId: taskTypeId,
        loadedId: null,
        status: 'loading' as const,
        module: null,
        error: null,
        staleDeployment: false,
      }
      : {
        requestedId: null,
        loadedId: null,
        status: 'idle' as const,
        module: null,
        error: null,
        staleDeployment: false,
      };

  return {
    descriptor,
    module: current.module,
    status: current.status,
    error: current.error,
    staleDeployment: current.staleDeployment,
    retry,
  };
}
